/**
 * Connect your Mac — the person creates a tenant API token for the session
 * read-back mirror and copies it once.
 *
 * The secret from POST lives only in this component's state. It is never
 * written to localStorage or sessionStorage, and it is dropped when the
 * reveal is closed or this card unmounts. List and revoke render metadata only.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { getUser } from '@/lib/auth';
import {
  MAC_SESSION_READBACK_LABEL,
  createTenantApiToken,
  listTenantApiTokens,
  revokeTenantApiToken,
  type TenantApiTokenMetadata,
} from '@/api/tenantApiTokens';

export const MAC_STORE_INSTRUCTION =
  "Store it on your Mac: security add-generic-password -s com.gcharris.ces.session-readback-mirror -a director -w '<paste>'";

const OWNER_NOTE = 'Only the account owner can create or revoke a token.';

/**
 * A known tenant role other than owner is read-only. An unknown role stays
 * editable until the server answers 403 — that response is the authority.
 */
function knownRoleAllowsWrite(role: string | null | undefined): boolean {
  const normalized = role?.trim().toLowerCase() ?? '';
  if (!normalized) return true;
  return normalized === 'owner';
}

function isForbidden(err: unknown): boolean {
  return typeof err === 'object' && err !== null && 'status' in err && (err as { status: unknown }).status === 403;
}

/** Keep the metadata fields the list is allowed to show. Drop anything else. */
function presentToken(row: TenantApiTokenMetadata): TenantApiTokenMetadata {
  return {
    id: row.id,
    label: row.label,
    token_prefix: row.token_prefix,
    last_used_at: row.last_used_at,
    created_at: row.created_at,
    revoked_at: row.revoked_at,
    active: row.active,
  };
}

function showWhen(value: string | null, empty: string): string {
  return value && value.trim() ? value : empty;
}

export const MacSessionReadbackCard: React.FC = () => {
  const roleAllows = knownRoleAllowsWrite(getUser()?.tenant?.role);
  const [writeLocked, setWriteLocked] = useState(false);
  const canWrite = roleAllows && !writeLocked;

  const [tokens, setTokens] = useState<TenantApiTokenMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const lockWrites = useCallback(() => {
    setWriteLocked(true);
    setRevealedSecret(null);
    setCopied(false);
  }, []);

  const fetchTokens = useCallback(async () => {
    const response = await listTenantApiTokens();
    setTokens((response.tokens ?? []).map(presentToken));
    setLoadError(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchTokens()
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isForbidden(err)) {
          setWriteLocked(true);
          setTokens([]);
          setLoadError(null);
          return;
        }
        setLoadError('Could not load tokens.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      setRevealedSecret(null);
    };
  }, [fetchTokens]);

  const closeReveal = () => {
    setRevealedSecret(null);
    setCopied(false);
  };

  const onCreate = async () => {
    if (!canWrite || creating) return;
    setActionError(null);
    setCreating(true);
    setRevealedSecret(null);
    setCopied(false);
    try {
      const created = await createTenantApiToken(MAC_SESSION_READBACK_LABEL);
      setRevealedSecret(created.secret);
      await fetchTokens();
    } catch (err) {
      setRevealedSecret(null);
      if (isForbidden(err)) {
        lockWrites();
        return;
      }
      setActionError('Could not create the token.');
    } finally {
      setCreating(false);
    }
  };

  const onRevoke = async (tokenId: string) => {
    if (!canWrite || revokingId) return;
    setActionError(null);
    setRevokingId(tokenId);
    try {
      await revokeTenantApiToken(tokenId);
      await fetchTokens();
    } catch (err) {
      if (isForbidden(err)) {
        lockWrites();
        return;
      }
      setActionError('Could not revoke the token.');
    } finally {
      setRevokingId(null);
    }
  };

  const onCopy = async () => {
    if (!revealedSecret) return;
    try {
      await navigator.clipboard.writeText(revealedSecret);
      setCopied(true);
    } catch {
      setCopied(false);
      setActionError('Could not copy the token.');
    }
  };

  return (
    <div
      className="rounded border border-gray-800 bg-gray-900/20 p-4"
      data-testid="connect-your-mac"
    >
      <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-gray-300">Connect your Mac</h3>
      <p className="mt-2 text-sm text-gray-400">
        Create a token for the session read-back mirror. The secret is shown once, here, and then it is gone.
      </p>

      {!loading && !canWrite ? <p className="mt-3 text-xs font-mono text-amber-400">{OWNER_NOTE}</p> : null}

      {loading ? (
        <p className="mt-3 text-xs font-mono text-gray-500">Loading tokens…</p>
      ) : loadError ? (
        <p className="mt-3 text-sm text-red-200" role="alert">
          {loadError}
        </p>
      ) : (
        <div className="mt-4" data-testid="mac-token-list">
          {tokens.length === 0 ? (
            <p className="text-sm text-gray-500">No tokens yet.</p>
          ) : (
            <table className="w-full text-left text-xs font-mono text-gray-300">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="py-1 pr-3 font-normal">Label</th>
                  <th className="py-1 pr-3 font-normal">Created</th>
                  <th className="py-1 pr-3 font-normal">Last used</th>
                  <th className="py-1 pr-3 font-normal">Revoked</th>
                  {canWrite ? <th className="py-1 font-normal" /> : null}
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => {
                  const revoked = Boolean(token.revoked_at) || token.active === false;
                  return (
                    <tr key={token.id} className="border-t border-gray-800">
                      <td className="py-2 pr-3 align-top">
                        <div>{token.label}</div>
                        <div className="text-[10px] text-gray-500">{token.token_prefix}</div>
                      </td>
                      <td className="py-2 pr-3 align-top">{showWhen(token.created_at, '—')}</td>
                      <td className="py-2 pr-3 align-top">{showWhen(token.last_used_at, 'Never')}</td>
                      <td className="py-2 pr-3 align-top">{showWhen(token.revoked_at, '—')}</td>
                      {canWrite ? (
                        <td className="py-2 align-top text-right">
                          {revoked ? null : (
                            <button
                              type="button"
                              className="text-[10px] font-mono text-red-400 hover:underline disabled:opacity-40"
                              onClick={() => void onRevoke(token.id)}
                              disabled={revokingId === token.id}
                            >
                              {revokingId === token.id ? 'Revoking…' : `Revoke ${token.label}`}
                            </button>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {canWrite ? (
        <button
          type="button"
          className="mt-4 rounded border border-[#39FF14]/30 bg-[#39FF14]/10 px-3 py-1.5 text-[11px] font-mono text-[#39FF14] transition-colors hover:bg-[#39FF14]/20 disabled:opacity-40"
          onClick={() => void onCreate()}
          disabled={creating || loading}
        >
          {creating ? 'Creating…' : 'Create'}
        </button>
      ) : null}

      {actionError ? (
        <p className="mt-3 text-sm text-red-200" role="alert">
          {actionError}
        </p>
      ) : null}

      {revealedSecret ? (
        <div className="mt-4 space-y-2" data-testid="mac-token-reveal">
          <label className="block text-[10px] font-mono uppercase tracking-wider text-gray-500" htmlFor="mac-token-secret">
            Token secret
          </label>
          <input
            id="mac-token-secret"
            aria-label="Token secret"
            className="w-full rounded border border-gray-700 bg-black/20 px-3 py-2 font-mono text-sm text-gray-100"
            readOnly
            value={revealedSecret}
            autoComplete="off"
            spellCheck={false}
          />
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded border border-gray-700 px-2 py-1 text-[11px] font-mono text-gray-200"
              onClick={() => void onCopy()}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              className="rounded border border-gray-700 px-2 py-1 text-[11px] font-mono text-gray-200"
              onClick={closeReveal}
            >
              Close
            </button>
          </div>
          <p className="text-sm leading-6 text-gray-300" data-testid="mac-token-command">
            {MAC_STORE_INSTRUCTION}
          </p>
        </div>
      ) : null}
    </div>
  );
};
